import { useBreakpoint } from "@light-technology/components";
import { InfoCircle, LogOut01 } from "@untitledui/icons";
import { formatDistanceToNow, isPast } from "date-fns";
import { useEffect, useState } from "react";
import { useAuth } from "../state/hooks";
import { exitStaffSession, type StaffSession } from "../utilities/staffSession";

type StaffSessionBannerProps = {
  session: StaffSession;
  onExpired: () => void;
};

export const StaffSessionBanner = ({
  session,
  onExpired,
}: StaffSessionBannerProps) => {
  const isMd = useBreakpoint("md");
  const { organization } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(
    formatDistanceToNow(session.expires),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPast(session.expires)) {
        onExpired();
      } else {
        setTimeRemaining(formatDistanceToNow(session.expires));
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [session, onExpired]);

  const handleExit = () => {
    exitStaffSession();
  };

  if (isMd) {
    return (
      <div className="flex flex-none items-center justify-between border-b border-secondary bg-primary px-xl py-md">
        <div className="flex items-center gap-md">
          <InfoCircle className="size-5 text-fg-quaternary" />
          <span className="text-md text-primary">
            You're temporarily accessing the {organization.name} org.
          </span>
          <span className="text-md text-tertiary">
            ({timeRemaining} remaining)
          </span>
        </div>
        <button
          onClick={handleExit}
          className="flex items-center gap-sm text-md text-primary hover:text-secondary"
        >
          <LogOut01 className="size-5" />
          <span>Return to admin</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-none flex-col gap-xs border-b border-secondary bg-primary px-lg py-md">
      <div className="flex items-center justify-between gap-md">
        <span className="truncate text-md text-primary">
          You're temporarily accessing the {organization.name} org.
        </span>
        <button
          onClick={handleExit}
          className="flex-none text-fg-quaternary hover:text-fg-secondary"
        >
          <LogOut01 className="size-5" />
        </button>
      </div>
      <span className="text-md text-tertiary">({timeRemaining} remaining)</span>
    </div>
  );
};
